import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AccountCircle,
  Block,
  CalendarToday,
  Delete,
  Edit,
  History,
  Payment,
  PersonAdd,
  Refresh,
  Save,
  Search,
  Visibility,
} from '@mui/icons-material';
import { customModal } from './CustomModal';

const formatDateTime = (value) => value
  ? new Date(value).toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  : '-';

const formatCurrency = (value) => `INR ${Number(value || 0).toLocaleString('en-IN')}`;

const statusColor = (status) => {
  if (status === 'completed' || status === 'active') return 'success';
  if (status === 'cancelled' || status === 'failed' || status === 'blocked') return 'error';
  if (status === 'pending' || status === 'searching_driver') return 'warning';
  return 'default';
};

const customerStatus = (customer) => (customer?.isBlocked ? 'blocked' : 'active');

const emptyCustomerForm = { _id: '', name: '', phoneNumber: '', email: '', address: '', language: 'en' };

const InfoRow = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', py: 0.5 }}>
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 130, flexShrink: 0 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontFamily={mono ? 'monospace' : 'inherit'} sx={{ wordBreak: 'break-word' }}>
      {value || '-'}
    </Typography>
  </Box>
);

const MetricCard = ({ label, value, helper }) => (
  <Card variant="outlined" sx={{ height: '100%' }}>
    <CardContent sx={{ py: 2 }}>
      <Typography variant="h5" fontWeight={900}>{value}</Typography>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      {helper && <Typography variant="caption" color="text.secondary">{helper}</Typography>}
    </CardContent>
  </Card>
);

const CustomerDetails = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyCustomerForm);
  const [createForm, setCreateForm] = useState(emptyCustomerForm);
  const [tab, setTab] = useState(0);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/admin/customers', { params: { search } });
      setCustomers(data.customers || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const totals = useMemo(() => customers.reduce((acc, customer) => {
    acc.bookings += customer.stats?.totalBookings || 0;
    acc.spent += customer.stats?.totalSpent || 0;
    if (customer.lastLoginAt) acc.loggedIn += 1;
    if (customer.isBlocked) acc.blocked += 1;
    return acc;
  }, { bookings: 0, spent: 0, loggedIn: 0, blocked: 0 }), [customers]);

  const syncCustomer = (updatedCustomer) => {
    setCustomers((current) => current.map((customer) => (
      customer._id === updatedCustomer._id
        ? { ...customer, ...updatedCustomer, stats: customer.stats }
        : customer
    )));
  };

  const loadCustomerDetails = async (customerId, showDialog = true) => {
    if (showDialog) setOpen(true);
    setSelected(null);
    setDetailError('');
    setDetailLoading(true);
    setTab(0);
    try {
      const { data } = await axios.get(`/admin/customers/${customerId}`);
      setSelected(data);
    } catch (err) {
      setDetailError(err.response?.data?.message || 'Failed to load customer details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleView = (customerId) => {
    loadCustomerDetails(customerId);
  };

  const refreshSelected = async (customerId) => {
    if (selected?.customer?._id === customerId) {
      await loadCustomerDetails(customerId, false);
    }
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    fetchCustomers();
  };

  const handleEditClick = (customer) => {
    const source = customer || selected?.customer;
    if (!source) return;
    setEditForm({
      _id: source._id || '',
      name: source.name || '',
      phoneNumber: source.phoneNumber || '',
      email: source.email || '',
      address: source.address || '',
      language: source.language || 'en',
    });
    setEditOpen(true);
  };

  const handleEditChange = (field) => (event) => {
    setEditForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleCreateChange = (field) => (event) => {
    setCreateForm((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleCreateClick = () => {
    setCreateForm(emptyCustomerForm);
    setActionError('');
    setNotice('');
    setCreateOpen(true);
  };

  const handleCreateCustomer = async () => {
    setActionLoading('create-customer');
    setActionError('');
    setNotice('');
    try {
      const { data } = await axios.post('/admin/customers', createForm);
      setCustomers((current) => [data.customer, ...current]);
      setNotice(data.message || 'Customer created successfully');
      setCreateOpen(false);
      setCreateForm(emptyCustomerForm);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to create customer');
    } finally {
      setActionLoading('');
    }
  };

  const handleSaveEdit = async () => {
    const customerId = selected?.customer?._id || editForm._id;
    if (!customerId) return;

    setActionLoading(`edit-${customerId}`);
    setActionError('');
    setNotice('');
    try {
      const { data } = await axios.put(`/admin/customers/${customerId}`, editForm);
      syncCustomer(data.customer);
      setNotice(data.message || 'Customer updated successfully');
      setEditOpen(false);
      await refreshSelected(customerId);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update customer');
    } finally {
      setActionLoading('');
    }
  };

  const handleBlockToggle = async (customer) => {
    const willBlock = !customer.isBlocked;
    let confirmed = false;
    if (!willBlock) {
      confirmed = await customModal.confirm('Unblock Customer', 'Unblock this customer and allow login again?', { type: 'info', confirmText: 'Unblock' });
      if (!confirmed) return;
    }
    const reason = willBlock ? window.prompt('Reason for blocking this customer?', customer.blockedReason || '') : '';
    if (willBlock && reason === null) return;

    setActionLoading(`block-${customer._id}`);
    setActionError('');
    setNotice('');
    try {
      const { data } = await axios.patch(`/admin/customers/${customer._id}/block`, {
        isBlocked: willBlock,
        reason,
      });
      syncCustomer(data.customer);
      setNotice(data.message || (willBlock ? 'Customer blocked successfully' : 'Customer unblocked successfully'));
      await refreshSelected(customer._id);
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to update customer access');
    } finally {
      setActionLoading('');
    }
  };

  const handleDelete = async (customer) => {
    const confirmed = await customModal.confirm('Delete Customer', `Delete ${customer.name || 'this customer'}? This hides the user from customer management and blocks future login.`, { type: 'error', confirmText: 'Delete' });
    if (!confirmed) return;

    setActionLoading(`delete-${customer._id}`);
    setActionError('');
    setNotice('');
    try {
      const { data } = await axios.delete(`/admin/customers/${customer._id}`);
      setCustomers((current) => current.filter((item) => item._id !== customer._id));
      setNotice(data.message || 'Customer deleted successfully');
      if (selected?.customer?._id === customer._id) {
        setOpen(false);
        setSelected(null);
      }
    } catch (err) {
      setActionError(err.response?.data?.message || 'Failed to delete customer');
    } finally {
      setActionLoading('');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Box>
          <Typography variant="h4" fontWeight={800}>Customer Details</Typography>
          <Typography variant="body2" color="text.secondary">Profile, orders, payments, login and activity history</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" startIcon={<PersonAdd />} onClick={handleCreateClick}>
            New Customer
          </Button>
          <Tooltip title="Refresh customers">
            <IconButton onClick={fetchCustomers} color="primary" size="large">
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {actionError && <Alert severity="error" sx={{ mb: 2 }}>{actionError}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <MetricCard label="Customers" value={customers.length} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <MetricCard label="Total Orders" value={totals.bookings} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <MetricCard label="Completed Payments" value={formatCurrency(totals.spent)} />
        </Grid>
        <Grid item xs={12} sm={3}>
          <MetricCard label="Blocked Users" value={totals.blocked} />
        </Grid>
      </Grid>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box component="form" onSubmit={handleSearchSubmit} sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search by name, phone, email, or address"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <Button type="submit" variant="contained">Search</Button>
        </Box>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
      ) : customers.length === 0 ? (
        <Alert severity="info">No customers found.</Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Customer</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Orders</TableCell>
                <TableCell>Paid</TableCell>
                <TableCell>Last Booking</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {customers.map((customer) => {
                const status = customerStatus(customer);
                const busy = actionLoading.endsWith(customer._id);

                return (
                  <TableRow key={customer._id} hover>
                    <TableCell>
                      <Typography fontWeight={700}>{customer.name || 'Customer'}</Typography>
                      <Typography variant="caption" color="text.secondary">{customer.email || customer.address || '-'}</Typography>
                    </TableCell>
                    <TableCell>{customer.phoneNumber || '-'}</TableCell>
                    <TableCell>
                      <Chip label={status} color={statusColor(status)} size="small" />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        <Chip label={`${customer.stats?.totalBookings || 0} total`} size="small" />
                        <Chip label={`${customer.stats?.completedBookings || 0} done`} size="small" color="success" variant="outlined" />
                      </Stack>
                    </TableCell>
                    <TableCell>{formatCurrency(customer.stats?.totalSpent)}</TableCell>
                    <TableCell>{formatDateTime(customer.stats?.lastBookingAt)}</TableCell>
                    <TableCell>{formatDateTime(customer.lastLoginAt)}</TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="View customer details">
                          <span>
                            <IconButton color="primary" onClick={() => handleView(customer._id)} disabled={busy}>
                              <Visibility />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Edit customer">
                          <span>
                            <IconButton color="primary" onClick={() => handleEditClick(customer)} disabled={busy}>
                              <Edit />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={customer.isBlocked ? 'Unblock customer' : 'Block customer'}>
                          <span>
                            <IconButton color={customer.isBlocked ? 'success' : 'warning'} onClick={() => handleBlockToggle(customer)} disabled={busy}>
                              <Block />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete customer">
                          <span>
                            <IconButton color="error" onClick={() => handleDelete(customer)} disabled={busy}>
                              <Delete />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <AccountCircle color="primary" />
          <Box sx={{ flexGrow: 1 }}>{selected?.customer?.name || 'Customer Details'}</Box>
          {selected?.customer && <Chip label={customerStatus(selected.customer)} color={statusColor(customerStatus(selected.customer))} size="small" />}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : detailError ? (
            <Alert severity="error">{detailError}</Alert>
          ) : selected && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={6} md={3}><MetricCard label="Total Orders" value={selected.summary.totalBookings} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Total Paid" value={formatCurrency(selected.summary.totalPaid)} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Feedback" value={selected.summary.feedbackCount} helper={selected.summary.averageRating ? `${selected.summary.averageRating} avg rating` : 'No rating'} /></Grid>
                <Grid item xs={6} md={3}><MetricCard label="Logins" value={selected.loginActivity.loginCount} /></Grid>
              </Grid>

              <Tabs value={tab} onChange={(_, value) => setTab(value)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tab icon={<AccountCircle />} iconPosition="start" label="Profile" />
                <Tab icon={<CalendarToday />} iconPosition="start" label="Orders" />
                <Tab icon={<Payment />} iconPosition="start" label="Payments" />
                <Tab icon={<History />} iconPosition="start" label="Activity" />
              </Tabs>

              {tab === 0 && <ProfileTab customer={selected.customer} loginActivity={selected.loginActivity} />}
              {tab === 1 && <BookingsTab bookings={selected.bookings} />}
              {tab === 2 && <PaymentsTab payments={selected.paymentDetails} summary={selected.summary} />}
              {tab === 3 && <ActivityTab activity={selected.activity} loginActivity={selected.loginActivity} feedbacks={selected.feedbacks} />}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selected?.customer && (
            <>
              <Button startIcon={<Edit />} onClick={() => handleEditClick(selected.customer)}>Edit</Button>
              <Button startIcon={<Block />} color={selected.customer.isBlocked ? 'success' : 'warning'} onClick={() => handleBlockToggle(selected.customer)}>
                {selected.customer.isBlocked ? 'Unblock' : 'Block'}
              </Button>
              <Button startIcon={<Delete />} color="error" onClick={() => handleDelete(selected.customer)}>Delete</Button>
            </>
          )}
          <Button variant="contained" onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Customer</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Name" value={createForm.name} onChange={handleCreateChange('name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth required label="Phone" value={createForm.phoneNumber} onChange={handleCreateChange('phoneNumber')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" value={createForm.email} onChange={handleCreateChange('email')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Language" value={createForm.language} onChange={handleCreateChange('language')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Address" value={createForm.address} onChange={handleCreateChange('address')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<PersonAdd />} onClick={handleCreateCustomer} disabled={actionLoading === 'create-customer'}>
            Add Customer
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ pt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Name" value={editForm.name} onChange={handleEditChange('name')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Phone" value={editForm.phoneNumber} onChange={handleEditChange('phoneNumber')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Email" value={editForm.email} onChange={handleEditChange('email')} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Language" value={editForm.language} onChange={handleEditChange('language')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={3} label="Address" value={editForm.address} onChange={handleEditChange('address')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<Save />} onClick={handleSaveEdit} disabled={actionLoading.startsWith('edit-')}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ProfileTab = ({ customer, loginActivity }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={6}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Profile Details</Typography>
        <InfoRow label="Customer ID" value={customer._id} mono />
        <InfoRow label="Name" value={customer.name} />
        <InfoRow label="Phone" value={customer.phoneNumber} />
        <InfoRow label="Email" value={customer.email} />
        <InfoRow label="Address" value={customer.address} />
        <InfoRow label="Language" value={customer.language} />
        <InfoRow label="Terms Accepted" value={customer.termsAccepted ? 'Yes' : 'No'} />
        <InfoRow label="Created" value={formatDateTime(customer.createdAt)} />
        <InfoRow label="Updated" value={formatDateTime(customer.updatedAt)} />
      </Paper>
    </Grid>
    <Grid item xs={12} md={6}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Access Status</Typography>
        <InfoRow label="Status" value={customerStatus(customer)} />
        <InfoRow label="Blocked At" value={formatDateTime(customer.blockedAt)} />
        <InfoRow label="Blocked Reason" value={customer.blockedReason} />
        <InfoRow label="Deleted At" value={formatDateTime(customer.deletedAt)} />
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Location and Login</Typography>
        <InfoRow label="Last Address" value={customer.lastAddress} />
        <InfoRow label="Latitude" value={customer.lastLatitude?.toString()} mono />
        <InfoRow label="Longitude" value={customer.lastLongitude?.toString()} mono />
        <InfoRow label="Last Login" value={formatDateTime(loginActivity.lastLoginAt)} />
        <InfoRow label="Login Count" value={loginActivity.loginCount?.toString()} />
        <InfoRow label="Push Token" value={loginActivity.hasFcmToken ? 'Registered' : 'Not registered'} />
        <InfoRow label="OTP Requests" value={loginActivity.otpRequestCount?.toString()} />
        <InfoRow label="OTP Blocked Until" value={formatDateTime(loginActivity.otpBlockedUntil)} />
      </Paper>
    </Grid>
  </Grid>
);

const BookingsTab = ({ bookings }) => bookings.length ? (
  <TableContainer component={Paper} variant="outlined">
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell>Booking</TableCell>
          <TableCell>Pickup</TableCell>
          <TableCell>Vehicle</TableCell>
          <TableCell>Driver</TableCell>
          <TableCell>Amount</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Created</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {bookings.map((booking) => (
          <TableRow key={booking._id} hover>
            <TableCell><Typography variant="caption" fontFamily="monospace">{String(booking._id).slice(-8).toUpperCase()}</Typography></TableCell>
            <TableCell sx={{ maxWidth: 260 }}><Typography variant="body2" noWrap title={booking.pickupAddress}>{booking.pickupAddress || '-'}</Typography></TableCell>
            <TableCell>{booking.vehicleNumber || booking.vehicle?.vehicleNumber || booking.vehicleType || '-'}</TableCell>
            <TableCell>{booking.driver?.name || '-'}</TableCell>
            <TableCell>{formatCurrency(booking.amount)}</TableCell>
            <TableCell><Chip label={booking.status || 'unknown'} color={statusColor(booking.status)} size="small" /></TableCell>
            <TableCell>{formatDateTime(booking.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
) : <Alert severity="info">No order history found for this customer.</Alert>;

const PaymentsTab = ({ payments, summary }) => payments.length ? (
  <Box>
    <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
      <Chip label={`Total amount ${formatCurrency(summary.totalAmount)}`} />
      <Chip label={`Paid ${formatCurrency(summary.totalPaid)}`} color="success" />
    </Stack>
    <TableContainer component={Paper} variant="outlined">
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Booking</TableCell>
            <TableCell>Payment ID</TableCell>
            <TableCell>Amount</TableCell>
            <TableCell>Paid Amount</TableCell>
            <TableCell>Payment Status</TableCell>
            <TableCell>Booking Status</TableCell>
            <TableCell>Date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {payments.map((payment) => (
            <TableRow key={payment.bookingId} hover>
              <TableCell><Typography variant="caption" fontFamily="monospace">{String(payment.bookingId).slice(-8).toUpperCase()}</Typography></TableCell>
              <TableCell>{payment.paymentId || '-'}</TableCell>
              <TableCell>{formatCurrency(payment.amount)}</TableCell>
              <TableCell>{formatCurrency(payment.paymentAmount)}</TableCell>
              <TableCell><Chip label={payment.paymentStatus} color={statusColor(payment.paymentStatus)} size="small" /></TableCell>
              <TableCell>{payment.bookingStatus}</TableCell>
              <TableCell>{formatDateTime(payment.createdAt)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>
) : <Alert severity="info">No payment history found for this customer.</Alert>;

const ActivityTab = ({ activity, loginActivity, feedbacks }) => (
  <Grid container spacing={2}>
    <Grid item xs={12} md={7}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Overall Activity</Typography>
        {activity.length ? activity.map((item, index) => (
          <Box key={`${item.type}-${item.at}-${index}`} sx={{ display: 'flex', gap: 1.5, py: 1.25, borderBottom: index === activity.length - 1 ? 'none' : '1px solid #eee' }}>
            <History fontSize="small" color="primary" />
            <Box>
              <Typography variant="body2" fontWeight={700}>{item.label.replace(/_/g, ' ')}</Typography>
              <Typography variant="caption" color="text.secondary">{formatDateTime(item.at)}</Typography>
              {item.meta && <Typography variant="body2" color="text.secondary">{item.meta}</Typography>}
            </Box>
          </Box>
        )) : <Alert severity="info">No activity recorded.</Alert>}
      </Paper>
    </Grid>
    <Grid item xs={12} md={5}>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Login Activity</Typography>
        <InfoRow label="Last Login" value={formatDateTime(loginActivity.lastLoginAt)} />
        <InfoRow label="Login Count" value={loginActivity.loginCount?.toString()} />
        <InfoRow label="Last OTP Request" value={formatDateTime(loginActivity.lastOtpRequestedAt)} />
        <InfoRow label="OTP Attempts" value={loginActivity.otpRequestCount?.toString()} />
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Feedback</Typography>
        {feedbacks.length ? feedbacks.map((feedback) => (
          <Box key={feedback._id} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
            <Typography variant="body2" fontWeight={700}>Rating {feedback.rating}</Typography>
            <Typography variant="caption" color="text.secondary">{formatDateTime(feedback.createdAt)}</Typography>
            <Typography variant="body2">{feedback.comment || '-'}</Typography>
          </Box>
        )) : <Typography variant="body2" color="text.secondary">No feedback submitted.</Typography>}
      </Paper>
    </Grid>
  </Grid>
);

export default CustomerDetails;